<template>
  <modal :open="internalOpen" :width="1200">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <form-card class="mt-7">
        <template #title>
          {{ $t(`pages.campaignGroupList.modal.general.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignGroupList.modal.general.description`) }}
        </template>
        <InputField
          v-model="rawCampaignGroup.name"
          :error="errors?.name"
          type="text"
          :label="$t('campaignGroup.name')"
          :placeholder="$t('campaignGroup.name')"
          class="my-4"
        />
        <text-area
          v-model="rawCampaignGroup.description"
          :error="errors?.description"
          :label="$t('campaignGroup.description.DEFAULT')"
          :placeholder="$t('campaignGroup.description.description')"
          class="my-4"
        />
        <multi-select
          v-model="rawCampaignGroup.ownerCompany"
          :error="errors?.company"
          :searchable="true"
          :label="$t('campaignGroup.ownerCompany')"
          :placeholder="$t('campaignGroup.ownerCompany')"
          :options="fetchCompanies"
          class="my-4 !w-full"
        />
        <InputField
          v-model="rawCampaignGroup.contactEmail"
          :error="errors?.contactEmail"
          type="email"
          :label="$t('campaignGroup.contactEmail')"
          :placeholder="$t('campaignGroup.contactEmail')"
          class="my-4"
        />
        <div class="flex gap-x-10">
          <Switch
            v-model="rawCampaignGroup.expressBooking"
            :label="$t('campaignGroup.expressBooking')"
          />
          <Switch
            v-model="rawCampaignGroup.contractConfirmed"
            :label="$t('campaignGroup.contractConfirmed')"
          />
        </div>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignGroupList.modal.translated.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignGroupList.modal.translated.description`) }}
        </template>
        <translation-accordion v-slot="{ translation }" v-model="rawCampaignGroup">
          <div class="flex flex-col gap-y-5">
            <InputField
              v-model="translation.title"
              type="text"
              name="title"
              :error="errors?.title"
              :label="$t('campaignGroup.translation.title')"
            />
            <HtmlEditor
              v-model="translation.text"
              name="text"
              :error="errors?.text"
              :label="$t('campaignGroup.translation.description.DEFAULT')"
            />
          </div>
        </translation-accordion>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignGroupList.modal.furtherInformation.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignGroupList.modal.furtherInformation.description`) }}
        </template>
        <div class="flex flex-col gap-y-4">
          <InputField
            v-model="rawCampaignGroup.defaultDriversCount"
            type="number"
            name="defaultDriversCount"
            :error="errors?.defaultDriversCount"
            :label="$t('campaignGroup.defaultDriversCount')"
            :placeholder="$t('campaignGroup.defaultDriversCount')"
            class="w-1/2"
          />
          <date-range
            v-model="rawCampaignGroup.date"
            :label="$t('campaignGroup.dateRange')"
            :errors="errors?.date"
            :placeholder="$t('campaignGroup.dateRange')"
            mode="datetime"
          />
        </div>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignGroupList.modal.campaignPictures.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignGroupList.modal.campaignPictures.description`) }}
        </template>
        <div class="overflow-x-auto snap-x py-5">
          <div class="w-[250%] flex gap-x-5">
            <box
              v-for="picture in pictures"
              :key="picture.name"
              :aspect="1"
              class="snap-start w-1/2 srink-0"
            >
              <medium
                v-model="picture.upload.file"
                class="h-full"
                :label="picture.name"
                :icon="PhotoIcon"
                :aspect="1"
                @click="picture.upload.open"
              />
            </box>
          </div>
        </div>
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignGroupList.modal.morePictures.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignGroupList.modal.morePictures.description`) }}
        </template>
        <Images
          v-model="images"
          :aspect="0.5"
          :label="$t('campaignGroup.uploadPictures')"
          @choose-file="openImageChooser"
        />
      </form-card>
      <form-card>
        <template #title>
          {{ $t(`pages.campaignGroupList.modal.driverFilter.DEFAULT`) }}
        </template>
        <template #description>
          {{ $t(`pages.campaignGroupList.modal.driverFilter.description`) }}
          <CheckFilter
            :filter="rawCampaignGroup.defaultQualifiedDriversFilter"
            :validation-function="validationFunction"
            route="/filters/campaign-driver"
            class="mt-4"
            :label="$t('pages.campaignList.modal.driverFilter.countLabel')"
            :additional-payload="{
              startDate: rawCampaignGroup.date.start,
              endDate: rawCampaignGroup.date.end,
            }"
          />
        </template>
        <LogicGate
          ref="driverFilter"
          v-model="rawCampaignGroup.defaultQualifiedDriversFilter"
          route="/filters/campaign-driver"
        />
      </form-card>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue';
import MultiSelect from '@utils/Multiselect.vue';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import InputField from '@utils/inputs/Input.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import Medium from '@utils/inputs/Medium.vue';
import Box from '@utils/Box.vue';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import PhotoIcon from '@images/photo.svg?component';
import LogicGate from '@utils/logicGate/LogicGate.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import { useVModel } from '@vueuse/core';
import { useValidation } from '@hooks/form';
import {
  array, boolean, date, number, object, string,
} from 'yup';
import { useI18n } from 'vue-i18n';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import DateRange from '@utils/inputs/DateRange.vue';
import Switch from '@utils/inputs/Switch.vue';
import { syncRef } from '@hooks/state';
import CheckFilter from '@utils/logicGate/CheckFilter.vue';
import { parseFilter } from '@utils/logicGate/logicFilter';
import HtmlEditor from '@utils/inputs/HtmlEditor.vue';
import type {
  CampaignGroup,
  CampaignGroupTranslations,
  MaybeHydra,
  Region,
  WbFile,
  Filter,
} from '@/entities/Entities';
import { Upload, useFileUpload, useSingleFileUpload } from '@/components/utils/upload/fileUpload';
import Images from '@/components/utils/inputs/Images.vue';

interface Props {
  open: boolean;
  campaignGroup?: CampaignGroup;
}

const { t } = useI18n();

const notify = useNotify();

const emit = defineEmits(['update:open', 'update:campaignGroup', 'edit-saved', 'new-saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalCampaignGroup = useVModel(props, 'campaignGroup', emit);

const isNew = computed(() => !props.campaignGroup);
const tKey = computed(() =>
  (isNew.value ? 'pages.campaignGroupList.modal.new' : 'pages.campaignGroupList.modal.edit'));

const initialData = (campaignGroup?: CampaignGroup) =>
  (campaignGroup
    ? {
      ...campaignGroup,
      date: {
        start: campaignGroup.defaultStartDate
          ? new Date(campaignGroup?.defaultStartDate)
          : new Date(),
        end: campaignGroup.defaultEndDate ? new Date(campaignGroup?.defaultEndDate) : new Date(),
      },
      defaultQualifiedDriversFilter: campaignGroup.defaultQualifiedDriversFilter ?? {
        logicGroups: [],
      },
      ownerCompany: campaignGroup.ownerCompany?.['@id'] ?? undefined,
    }
    : {
      name: undefined,
      title: undefined,
      description: undefined,
      ownerCompany: undefined,
      contactEmail: undefined,
      date: {
        start: new Date(),
        end: new Date(),
      },
      defaultDriversCount: undefined,
      contractConfirmed: false,
      expressBooking: false,
      translations: [{ title: undefined, text: undefined, language: 'de' }],
      defaultQualifiedDriversFilter: {
        logicGroups: [],
      },
    });

const prevFileLeft = computed(() =>
  (props.campaignGroup && props.campaignGroup.defaultPictureLeft
    ? Upload.from(props.campaignGroup.defaultPictureLeft)
    : undefined));
const prevFileRight = computed(() =>
  (props.campaignGroup && props.campaignGroup.defaultPictureRight
    ? Upload.from(props.campaignGroup.defaultPictureRight)
    : undefined));
const prevFileTop = computed(() =>
  (props.campaignGroup && props.campaignGroup.defaultPictureTop
    ? Upload.from(props.campaignGroup.defaultPictureTop)
    : undefined));
const prevFileBack = computed(() =>
  (props.campaignGroup && props.campaignGroup.defaultPictureBack
    ? Upload.from(props.campaignGroup.defaultPictureBack)
    : undefined));
const prevFileFront = computed(() =>
  (props.campaignGroup && props.campaignGroup.defaultPictureFront
    ? Upload.from(props.campaignGroup.defaultPictureFront)
    : undefined));

const {
  files: images,
  open: openImageChooser,
  uploadDiff: uploadImageDiff,
  loading: imagesSaving,
  getUpdatedFiles,
  reset: resetImages,
} = useFileUpload<WbFile>(
  computed(
    () =>
      props.campaignGroup?.additionalPhotos
        ?.filter((photo) => photo.file)
        ?.map((photo) => Upload.from(photo.file)) ?? [],
  ),
);

const pictures = reactive([
  {
    key: 'defaultPictureLeft',
    name: t('pages.campaignGroupList.modal.campaignPictures.pictures.left'),
    upload: useSingleFileUpload<WbFile>(prevFileLeft),
  },
  {
    key: 'defaultPictureRight',
    name: t('pages.campaignGroupList.modal.campaignPictures.pictures.right'),
    upload: useSingleFileUpload<WbFile>(prevFileRight),
  },
  {
    key: 'defaultPictureTop',
    name: t('pages.campaignGroupList.modal.campaignPictures.pictures.top'),
    upload: useSingleFileUpload<WbFile>(prevFileTop),
  },
  {
    key: 'defaultPictureFront',
    name: t('pages.campaignGroupList.modal.campaignPictures.pictures.front'),
    upload: useSingleFileUpload<WbFile>(prevFileFront),
  },
  {
    key: 'defaultPictureBack',
    name: t('pages.campaignGroupList.modal.campaignPictures.pictures.back'),
    upload: useSingleFileUpload<WbFile>(prevFileBack),
  },
]);

const rawCampaignGroup = syncRef(internalCampaignGroup, (campaignGroup) => ({
  ...initialData(campaignGroup),
}));

const {
  data: outputCampaignGroup,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    name: string().nullable().required(t('validation.notEmpty')),
    description: string().nullable().required(t('validation.notEmpty')),
    ownerCompany: string().nullable(),
    contactEmail: string()
      .nullable()
      .required(t('validation.notEmpty'))
      .email(t('validation.emailValid')),
    date: object({
      start: date().required(t('validation.notEmpty')),
      end: date().required(t('validation.notEmpty')),
    }),
    translations: array(
      object({
        title: string().nullable(),
        defaultLanguage: string().nullable(),
      }),
    ),
    contractConfirmed: boolean().required(t('validation.notEmpty')),
    expressBooking: boolean().required(t('validation.notEmpty')),
    defaultQualifiedDriversFilter: object().required(t('validation.notEmpty')),
    defaultDriversCount: number().nullable().required(t('validation.notEmpty')),
  }),
  rawCampaignGroup,
);

const fetchCompanies = collOptionsProvider<Region>(
  '/companies',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
  }),
);

const resetFiles = () => {
  for (const picture of pictures) {
    picture.upload.reset();
  }
};

function resetData() {
  rawCampaignGroup.value = { ...initialData() };
  setValidation(false);
  resetFiles();
  resetImages();
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalCampaignGroup.value = undefined;
}

const [exec, { loading: saving }] = useFetchAction();

const loading = ref(false);

const driverFilter = ref<LogicGate>(null);

const validationFunction = (data: MaybeHydra<Filter>): boolean => {
  if (!rawCampaignGroup.value.date.start || !rawCampaignGroup.value.date.end) {
    return false;
  }
  return driverFilter.value?.validateData(data);
};

const save = validatedFn(async () => {
  if (
    !outputCampaignGroup.value.defaultQualifiedDriversFilter ||
    !driverFilter.value.validateData(outputCampaignGroup.value.defaultQualifiedDriversFilter)
  ) {
    return;
  }

  try {
    loading.value = true;
    const getAdditionalImage = (fileId: string) =>
      props.campaignGroup?.additionalPhotos.find((photo) => photo?.file?.id === fileId);

    const uploadFiles = await Promise.all(
      pictures.map(async ({ upload }) => upload.getUpdatedFiles(await upload.uploadDiff())?.[0]),
    );

    const additionalImages = await uploadImageDiff();
    const newImages = getUpdatedFiles(additionalImages);
    console.log(newImages);

    const edit = !!props.campaignGroup;
    const url = edit ? props.campaignGroup['@id'] : '/campaign_groups';

    const savedCampaignGroup: CampaignGroup = await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: outputCampaignGroup.value.name,
        description: outputCampaignGroup.value.description,
        ownerCompany: outputCampaignGroup.value.ownerCompany,
        contactEmail: outputCampaignGroup.value.contactEmail,
        defaultStartDate: outputCampaignGroup.value.date.start,
        defaultEndDate: outputCampaignGroup.value.date.end,
        contractConfirmed: outputCampaignGroup.value.contractConfirmed,
        expressBooking: outputCampaignGroup.value.expressBooking,
        defaultDriversCount: outputCampaignGroup.value.defaultDriversCount,
        translations: (
          outputCampaignGroup.value.translations as MaybeHydra<CampaignGroupTranslations>[]
        )
          .filter((trans: MaybeHydra<CampaignGroupTranslations>) => trans.text || trans.title)
          .map((trans) => ({ ...trans, id: trans['@id'] ?? undefined })),
        additionalPhotos: newImages.map((image) => ({
          id: getAdditionalImage(image.file.id)?.['@id'],
          file: image.file['@id'],
        })),
        ...Object.fromEntries(
          pictures.map(({ key }, index) => [key, uploadFiles[index]?.file?.['@id'] ?? null]),
        ),
        defaultQualifiedDriversFilter: parseFilter(
          outputCampaignGroup.value.defaultQualifiedDriversFilter,
        ),
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit(`${edit ? 'edit' : 'new'}-saved`, savedCampaignGroup.id);
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  } finally {
    loading.value = false;
  }
});
</script>

<style lang="scss" scoped></style>
